import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { playSound } from '../audio';

interface LegalRightsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'LEGAL' | 'SOURCES';

export const LegalRightsModal: React.FC<LegalRightsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('LEGAL');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-sm overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="bg-[#fcfaf5] border-2 border-amber-600/80 rounded-2xl shadow-2xl max-w-xl w-full p-4 sm:p-6 text-stone-900 my-auto relative overflow-hidden flex flex-col max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-amber-800/20 shrink-0">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-xl bg-amber-100 border border-amber-300 flex items-center justify-center text-lg shadow-inner">
                {activeTab === 'LEGAL' ? '⚖️' : '📚'}
              </span>
              <div>
                <h3 className="text-base sm:text-lg font-black font-cinzel text-amber-950 uppercase tracking-wider leading-tight">
                  Mentions Légales & Sources
                </h3>
                <p className="text-[11px] text-stone-500 font-medium">
                  Quizz Kabyle : Aventures & Défis
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                playSound('click');
                onClose();
              }}
              className="w-8 h-8 rounded-full bg-stone-200 hover:bg-stone-300 text-stone-700 flex items-center justify-center font-bold text-sm transition-colors cursor-pointer"
              title="Fermer"
            >
              ✕
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-1.5 p-1 bg-[#efe7da] rounded-xl border border-[#dfd2be] mt-3 shrink-0">
            <button
              type="button"
              onClick={() => {
                playSound('click');
                setActiveTab('LEGAL');
              }}
              className={`flex-1 py-2 px-3 rounded-lg font-black text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                activeTab === 'LEGAL'
                  ? 'bg-amber-800 text-white shadow-xs border border-amber-700'
                  : 'text-stone-700 hover:bg-amber-100/60'
              }`}
            >
              <span>⚖️</span>
              <span>Droits & Propriété</span>
            </button>
            <button
              type="button"
              onClick={() => {
                playSound('click');
                setActiveTab('SOURCES');
              }}
              className={`flex-1 py-2 px-3 rounded-lg font-black text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                activeTab === 'SOURCES'
                  ? 'bg-amber-800 text-white shadow-xs border border-amber-700'
                  : 'text-stone-700 hover:bg-amber-100/60'
              }`}
            >
              <span>📚</span>
              <span>Sources & IA Gemini</span>
            </button>
          </div>

          {/* Content Body */}
          <div className="py-3.5 space-y-3.5 text-xs text-stone-700 leading-relaxed overflow-y-auto pr-1 flex-1">
            {activeTab === 'LEGAL' ? (
              <>
                {/* Copyright Banner */}
                <div className="bg-amber-50/90 border border-amber-300/80 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-amber-950 font-black text-xs sm:text-sm">
                    <span>© 2026 Yoann Baron</span>
                    <span className="text-amber-600">•</span>
                    <span className="uppercase tracking-wider text-[11px] text-amber-800">Tous droits réservés</span>
                  </div>
                  <p className="text-[11px] text-stone-600 mt-1">
                    Conception originale, développement, interfaces graphiques, narration et mini-jeux créés et détenus par <strong>Yoann Baron</strong>.
                  </p>
                </div>

                {/* Propriété de l'œuvre et des concepts */}
                <div className="space-y-1">
                  <h4 className="font-bold text-amber-900 uppercase tracking-wide text-[11px] flex items-center gap-1.5">
                    <span>🛡️</span> Protection de l'œuvre & des mécanismes
                  </h4>
                  <p className="text-[11px] text-stone-600">
                    L'ensemble du code source, des parcours d'apprentissage, du mode aventure (<em>« La Légende des Mots Oubliés »</em>), des énigmes de sanctuaires, des mécaniques de quiz et des interfaces interactives constituent une création protégée par les lois internationales sur le droit d'auteur et la propriété intellectuelle.
                  </p>
                </div>

                {/* Marque & Franchise */}
                <div className="space-y-1">
                  <h4 className="font-bold text-amber-900 uppercase tracking-wide text-[11px] flex items-center gap-1.5">
                    <span>ⵣ</span> Marque & Déclinaison
                  </h4>
                  <p className="text-[11px] text-stone-600">
                    Le concept et la structure de l'application s'inscrivent dans la franchise éducative <strong>« Quizz [Langue] : Aventures & Défis »</strong>. Toute reproduction, copie intégrale ou réutilisation des mécanismes de jeu sans autorisation préalable de l'auteur est interdite.
                  </p>
                </div>

                {/* Contributions de la communauté */}
                <div className="space-y-1 bg-stone-100/80 border border-stone-200 rounded-xl p-2.5">
                  <h4 className="font-bold text-stone-900 uppercase tracking-wide text-[11px] flex items-center gap-1.5">
                    <span>🤝</span> Contributions & Enrichissement linguistique
                  </h4>
                  <p className="text-[11px] text-stone-600">
                    Les suggestions linguistiques, lexiques ou contes proposés par la communauté sont soumis à l'évaluation et à la sélection exclusive de l'auteur. Les contributions validées sont intégrées à titre gracieux et bénévole pour enrichir l'expérience collective, avec mention des contributeurs dans les remerciements.
                  </p>
                </div>

                {/* Conception IA Google */}
                <div className="space-y-1 bg-gradient-to-r from-blue-50/80 to-amber-50/80 border border-blue-200/70 rounded-xl p-2.5">
                  <h4 className="font-bold text-blue-950 uppercase tracking-wide text-[11px] flex items-center gap-1.5">
                    <span>✨</span> Conception assistée par Google AI Studio
                  </h4>
                  <p className="text-[11px] text-stone-600">
                    Cette application a été construite et enrichie avec l'aide des technologies et modèles d'intelligence artificielle de <strong>Google AI Studio</strong> (Gemini).
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Introduction Sources & IA */}
                <div className="bg-amber-50/90 border border-amber-300/80 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-amber-950 font-black text-xs sm:text-sm">
                    <span>🤖 Intelligence Artificielle & Corpus de Référence</span>
                  </div>
                  <p className="text-[11px] text-stone-600 mt-1">
                    Les mots, traductions, exemples d'utilisation, pluriels et explications contextuelles générés par <strong>Gemini</strong> (Google AI Studio) s'appuient sur les corpus et ouvrages académiques majeurs de la langue kabyle (Tamazight).
                  </p>
                </div>

                {/* Ouvrages & Dictionnaires de référence */}
                <div className="space-y-2">
                  <h4 className="font-bold text-amber-900 uppercase tracking-wide text-[11px] flex items-center gap-1.5">
                    <span>📖</span> Dictionnaires & Travaux linguistiques de référence
                  </h4>
                  <ul className="space-y-1.5 text-[11px] text-stone-600 list-disc list-inside bg-stone-50 border border-stone-200 rounded-xl p-2.5">
                    <li>
                      <strong className="text-stone-900">Dictionnaire Kabyle-Français</strong> – <em>Jean-Marie Dallet</em> (Parler des At Mengellat, référence lexicographique fondamentale).
                    </li>
                    <li>
                      <strong className="text-stone-900">Dictionnaire des racines berbères</strong> (formes attestées) – <em>Professeur Mohand Akli Haddadou</em>.
                    </li>
                    <li>
                      <strong className="text-stone-900">Dictionnaire de Tamazight</strong> & grammaire kabyle – <em>Salem Chaker</em> & publications du <strong>Centre de Recherche Berbère (CRB / INALCO)</strong>.
                    </li>
                    <li>
                      <strong className="text-stone-900">Travaux et lexiques de l'Académie Berbère (Agraw Imazighen)</strong> et de l'<strong>HCA</strong> (Haut Commissariat à l'Amazighité).
                    </li>
                    <li>
                      <strong className="text-stone-900">Corpus oraux traditionnels</strong> : contes populaires, maximes (Inzan), poésies kabyles anciennes et proverbes du terroir kabyle.
                    </li>
                  </ul>
                </div>

                {/* Normalisation Orthographique */}
                <div className="space-y-1">
                  <h4 className="font-bold text-amber-900 uppercase tracking-wide text-[11px] flex items-center gap-1.5">
                    <span>🔤</span> Notation & Normalisation orthographique
                  </h4>
                  <p className="text-[11px] text-stone-600">
                    L'écriture kabyle en caractères latins standardisés respecte les conventions adoptées par la recherche linguistique internationale (lettres emphatiques <em>ḍ, ṭ, ṣ, ẓ</em>, spirantes et fricatives <em>ɣ, Ɛ, č, ğ</em>) ainsi que la notation Tifinagh ⵣ officielle.
                  </p>
                </div>

                {/* Synthèse Vocale : Amazigh TTS MMS */}
                <div className="space-y-1 bg-gradient-to-r from-emerald-50/90 to-amber-50/90 border border-emerald-300/80 rounded-xl p-2.5">
                  <h4 className="font-bold text-emerald-950 uppercase tracking-wide text-[11px] flex items-center gap-1.5">
                    <span>🎙️</span> Synthèse Vocale : Amazigh TTS MMS
                  </h4>
                  <p className="text-[11px] text-stone-700">
                    Pour la prononciation et les exercices d'écoute, l'application utilise la technologie <strong>Amazigh TTS MMS</strong> (<em>Massively Multilingual Speech</em>), un modèle de synthèse vocale avancé dédié aux phonèmes, accents et sonorités propres à la langue amazighe / kabyle.
                  </p>
                </div>

                {/* Mention Google AI Studio */}
                <div className="space-y-1 bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border border-blue-200 rounded-xl p-2.5">
                  <h4 className="font-bold text-blue-950 uppercase tracking-wide text-[11px] flex items-center gap-1.5">
                    <span>🚀</span> Développement & Modèles Google AI Studio
                  </h4>
                  <p className="text-[11px] text-stone-600">
                    L'architecture, le code applicatif et les modules d'entraînement interactifs ont été conçus et développés avec l'assistance de l'IA via <strong>Google AI Studio</strong>, assurant une expérience éducative moderne et accessible.
                  </p>
                </div>
              </>
            )}

            {/* Version Bêta */}
            <div className="text-[10px] text-stone-500 italic pt-1 border-t border-stone-200">
              Application actuellement en version Bêta d'évaluation publique. Vos données et progression sont sauvegardées localement.
            </div>
          </div>

          {/* Footer Action */}
          <div className="pt-3 border-t border-amber-800/20 flex justify-end shrink-0">
            <button
              type="button"
              onClick={() => {
                playSound('click');
                onClose();
              }}
              className="w-full sm:w-auto bg-amber-800 hover:bg-amber-900 text-white font-black text-xs uppercase tracking-wider px-5 py-2.5 rounded-xl transition-all shadow-sm active:scale-95 cursor-pointer border border-amber-700"
            >
              Fermer
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
